/**
 * ScrollbackBannerVanilla - Shows a dismissible banner when scrollback is restored.
 * Displays line count and relative timestamp.
 */

export interface ScrollbackBannerVanillaOptions {
  container: HTMLElement;
  onDismiss: () => void;
}

const ICONS = {
  scroll: '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M15 12h-5"/><path d="M15 8h-5"/><path d="M19 17V5a2 2 0 0 0-2-2H4"/><path d="M8 21h12a2 2 0 0 0 2-2v-1a1 1 0 0 0-1-1H11a1 1 0 0 0-1 1v1a2 2 0 1 1-4 0V5a2 2 0 1 0-4 0v2"/></svg>',
  x: '<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>',
};

function formatTimeAgo(timestamp: number): string {
  const now = Math.floor(Date.now() / 1000);
  const diff = now - timestamp;

  if (diff < 60) return 'just now';
  if (diff < 3600) {
    const mins = Math.floor(diff / 60);
    return `${mins} minute${mins !== 1 ? 's' : ''} ago`;
  }
  if (diff < 86400) {
    const hours = Math.floor(diff / 3600);
    return `${hours} hour${hours !== 1 ? 's' : ''} ago`;
  }
  const days = Math.floor(diff / 86400);
  return `${days} day${days !== 1 ? 's' : ''} ago`;
}

export class ScrollbackBannerVanilla {
  private container: HTMLElement;
  private onDismiss: () => void;
  private visible: boolean = false;

  constructor(options: ScrollbackBannerVanillaOptions) {
    this.container = options.container;
    this.onDismiss = options.onDismiss;
    this.setupEventListeners();
    this.render();
  }

  /**
   * Show the banner with scrollback info.
   */
  show(lineCount: number, savedAt?: number): void {
    this.visible = true;
    const timeStr = savedAt ? formatTimeAgo(savedAt) : 'a previous session';
    this.container.innerHTML = `
      <div class="sb-banner" role="status" aria-live="polite">
        <span class="sb-banner-icon">${ICONS.scroll}</span>
        <span class="sb-banner-text">Restored ${lineCount.toLocaleString()} line${lineCount !== 1 ? 's' : ''} from ${this.esc(timeStr)}</span>
        <button class="sb-banner-dismiss" data-action="dismiss" title="Dismiss" aria-label="Dismiss scrollback banner">${ICONS.x}</button>
      </div>
    `;
  }

  /**
   * Hide the banner.
   */
  hide(): void {
    this.visible = false;
    this.container.innerHTML = '';
  }

  isVisible(): boolean {
    return this.visible;
  }

  private esc(str: string): string {
    const d = document.createElement('div');
    d.textContent = str;
    return d.innerHTML;
  }

  private render(): void {
    if (!this.visible) {
      this.container.innerHTML = '';
    }
  }

  private setupEventListeners(): void {
    this.container.addEventListener('click', (e: Event) => {
      const target = e.target as HTMLElement;
      if (target.closest('[data-action="dismiss"]')) {
        this.hide();
        this.onDismiss();
      }
    });
  }

  dispose(): void {
    this.container.innerHTML = '';
  }
}
